/**
 * CrmOpportunitiesView — the pursuit pipeline: a weighted-pipeline metric strip, stage filter pills,
 * search, and a value-sorted list. Rows open CrmOpportunityDetailModal (stage management + interactions).
 */
import { useEffect, useRef, useState } from 'react'
import { getOpportunities, searchAllOpportunities, PURSUIT_STAGES, STAGE_PROB } from '../../services/opportunities.js'
import { getRecentOpportunityInteractions } from '../../services/interactions.js'
import { URGENCY } from './crmLeadNextStep.js'
import CrmOpportunityDetailModal from './CrmOpportunityDetailModal.jsx'
import CrmPipelineInsights from './CrmPipelineInsights.jsx'
import { syncOpen } from './crmSync.js'

const STAGE_COLOR = {
  'Business Development': '#8a8f98', 'Gathering Info': '#3A6EA5', 'Requested': '#2B7A9E',
  'Under Review': '#1D9E75', 'Negotiation': '#9a6700', 'Approved': '#1f9d57', 'Proposal Posted': '#7c3aed',
  'Pending Countersignature': '#B7651A',
}
const PROPOSAL_STAGES = ['Proposal Posted', 'Under Review']

// Cheap per-row urgency heuristic (days since last activity + a per-stage floor) — NOT the full
// opportunityNextStep engine, which needs per-row countersign-status fetches too expensive for a
// whole-list render. Mirrors how CrmLeadsView sorts by the precomputed daysSinceContact while only
// the detail modal runs the full rule engine. "Worse" always wins when combining with the floor.
const URGENCY_RANK = { red: 0, amber: 1, teal: 2, gray: 3 }
const STAGE_FLOOR = { 'Pending Countersignature': 'amber' }

function rowUrgency(o, lastActivityMap) {
  const last = lastActivityMap[o.id] || o.createdTime
  const days = last ? Math.floor((Date.now() - new Date(last).getTime()) / 86400000) : null
  let level = days === null ? 'amber' : days > 14 ? 'red' : days > 7 ? 'amber' : 'teal'
  const floor = STAGE_FLOOR[o.stage]
  if (floor && URGENCY_RANK[floor] < URGENCY_RANK[level]) level = floor
  return level
}

function money(n) { return n ? '$' + Math.round(n).toLocaleString() : '—' }

export default function CrmOpportunitiesView() {
  const [opps, setOpps] = useState(null)
  const [stage, setStage] = useState('All')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(null)
  const [allResults, setAllResults] = useState(null)   // search across ALL stages (find Executed projects)
  const [lastActivity, setLastActivity] = useState({})   // projectId -> most recent interaction date
  const loadedRef = useRef(false)

  // syncOpen keeps an already-open detail modal pointed at its refreshed row — the list alone
  // is not enough, since the modal holds the object captured when the row was clicked.
  function load() { setOpps(null); getOpportunities().then(list => { setOpps(list); setOpen(syncOpen(list)) }).catch(() => setOpps([])) }
  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    load()
    // One bulk query for the whole list's urgency tier — never per-opportunity (Airtable's ~5
    // req/sec limit). Reduce client-side to the most recent touch per project.
    getRecentOpportunityInteractions().then(list => {
      const map = {}
      for (const ix of list) {
        if (!ix.projectIds || !ix.projectIds.length || !ix.date) continue
        for (const pid of ix.projectIds) {
          if (!map[pid] || ix.date > map[pid]) map[pid] = ix.date
        }
      }
      setLastActivity(map)
    }).catch(() => {})
  }, [])

  // When searching, also look up projects in any stage (Executed included) so ASRs can be issued.
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setAllResults(null); return }
    let live = true
    searchAllOpportunities(term).then(r => { if (live) setAllResults(r) }).catch(() => { if (live) setAllResults([]) })
    return () => { live = false }
  }, [q])

  const list = Array.isArray(opps) ? opps : []
  const weightedTotal = list.reduce((s, o) => s + (o.weighted || 0), 0)
  const unweightedTotal = list.reduce((s, o) => s + (o.fee || 0), 0)
  const proposalsOut = list.filter(o => PROPOSAL_STAGES.indexOf(o.stage) !== -1).length

  const searching = q.trim().length >= 2
  const filtered = list
    .filter(o => stage === 'All' ? true : o.stage === stage)
    .filter(o => !q.trim() || `${o.name} ${o.projectType} ${o.address}`.toLowerCase().includes(q.toLowerCase()))
    .map(o => ({ ...o, urgency: rowUrgency(o, lastActivity) }))
    // Most urgent first — the "always working the pipeline" ask: someone manning the shop should
    // see what needs attention before what's merely biggest. Weighted value breaks ties.
    .sort((a, b) => (URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency]) || ((b.weighted || 0) - (a.weighted || 0)))
  // While searching, show all-stage matches (so Executed projects appear — needed to issue ASRs).
  const showing = searching && allResults ? allResults.slice().sort((a, b) => String(a.name).localeCompare(String(b.name))) : filtered

  return (
    <div style={S.wrap}>
      <div style={S.metrics}>
        <Metric label="Weighted pipeline" value={money(weightedTotal)} hint="Each fee × its stage probability — your realistic forecast." big />
        <Metric label="Unweighted" value={money(unweightedTotal)} hint="Sum of all estimated fees, best case." />
        <Metric label="Proposals out" value={String(proposalsOut)} hint="Posted or under review — nearest-term wins." />
        <Metric label="Active" value={String(list.length)} hint="Open pursuits." />
      </div>

      <CrmPipelineInsights />

      <div style={S.toolbar}>
        <div style={S.pills}>
          <button style={pill(stage === 'All')} onClick={() => setStage('All')}>All</button>
          {PURSUIT_STAGES.map(s => <button key={s} style={pill(stage === s)} onClick={() => setStage(s)}>{s}</button>)}
        </div>
        <div style={{ flex: 1 }} />
        <input style={S.search} placeholder="Search opportunities…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      <div style={S.count}>
        {opps === null ? 'Loading…' : searching ? `${showing.length} match${showing.length === 1 ? '' : 'es'} · all stages (incl. Executed)` : `${showing.length} opportunit${showing.length === 1 ? 'y' : 'ies'}`}
      </div>

      <div style={S.list}>
        {opps === null ? null : showing.length === 0 ? (
          <div style={S.empty}>{searching ? 'No projects match.' : `No opportunities${stage !== 'All' ? ` in “${stage}”` : ''}.`}</div>
        ) : showing.map(o => (
          <button key={o.id} style={S.row} onClick={() => setOpen(o)}>
            {o.urgency && (
              <span style={{ ...S.urgencyDot, background: (URGENCY[o.urgency] || URGENCY.amber).accent }}
                title={(URGENCY[o.urgency] || URGENCY.amber).label} />
            )}
            <span style={S.nameCol}>
              <span style={S.name}>{o.name}</span>
              {(o.projectType || o.address) && <span style={S.sub}>{[o.projectType, o.address].filter(Boolean).join(' · ')}</span>}
            </span>
            <span style={{ ...S.stageBadge, background: (STAGE_COLOR[o.stage] || '#5a7884') + '1a', color: STAGE_COLOR[o.stage] || '#5a7884' }}>{o.displayStage || o.stage || '—'}</span>
            <span style={S.fees}>
              <span style={S.weighted}>{money(o.weighted)}</span>
              <span style={S.feeRaw}>{money(o.fee)}{o.prob != null ? ` · ${Math.round(o.prob * 100)}%` : ''}</span>
            </span>
          </button>
        ))}
      </div>

      {open && <CrmOpportunityDetailModal opp={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  )
}

function Metric({ label, value, hint, big }) {
  return (
    <div style={S.metric} title={hint}>
      <div style={S.metricLabel}>{label}</div>
      <div style={{ ...S.metricValue, ...(big ? { fontSize: 24 } : {}) }}>{value}</div>
    </div>
  )
}

function pill(on) {
  return { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: on ? 600 : 400, color: on ? '#fff' : 'var(--aj-text-mid)', background: on ? '#07272D' : 'var(--aj-bg-muted)', border: 'none', borderRadius: 20, padding: '5px 13px', cursor: 'pointer' }
}

const S = {
  wrap: { maxWidth: 980, margin: '0 auto', padding: '18px 20px 60px' },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 },
  metric: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: '12px 14px' },
  metricLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', marginBottom: 6 },
  metricValue: { fontFamily: '"DM Mono", monospace', fontSize: 17, fontWeight: 500, color: 'var(--aj-text)' },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  pills: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  search: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 11px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, background: 'var(--aj-bg-elevated)', width: 200 },
  count: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', margin: '2px 2px 8px' },
  list: { display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-elevated)', borderRadius: 10, border: '0.5px solid var(--aj-border)', overflow: 'hidden' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer', textAlign: 'left', width: '100%' },
  urgencyDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0 },
  nameCol: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  name: { fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stageBadge: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '3px 10px', flexShrink: 0 },
  fees: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: 130, flexShrink: 0 },
  weighted: { fontFamily: '"DM Mono", monospace', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' },
  feeRaw: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-mid)', marginTop: 1 },
  empty: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '24px 14px', textAlign: 'center' },
}
